import { Injectable, OnDestroy, Optional } from '@angular/core';
import { Subject } from 'rxjs';

import { barcodeService } from '@app/modules/common/barcode';

/************************************************************************/
/* DESPACHADOR DE "KEYBOARD WEDGE" (lector de barras + pulseras RFID)    */
/*                                                                      */
/* Los lectores USB de códigos de barras y de pulseras RFID se presentan */
/* como un TECLADO: "teclean" el código muy deprisa y, según el modelo,  */
/* un Enter final. Como los dos lectores son indistinguibles tecla a     */
/* tecla (un EAN-13 y un tag de pulsera empiezan igual: dígitos rápidos) */
/* aquí hay UN ÚNICO listener de keydown que:                           */
/*   - escucha en fase de CAPTURA del documento: ve las teclas antes que */
/*     el numpad del PIN de camareros, el buscador o cualquier input;    */
/*   - acumula la RÁFAGA COMPLETA (hasta Enter o silencio) y se traga    */
/*     las teclas desde que detecta velocidad de lector;                 */
/*   - clasifica al final: exactamente RFID_TAG_LENGTH dígitos → pulsera */
/*     (OnTag / Capture); cualquier otra cosa → barcodeService.Inject.  */
/* Clasificar a mitad de ráfaga NO es posible: las teclas tragadas no se */
/* pueden "devolver". Un código de barras de exactamente 10 dígitos se   */
/* tomará por pulsera mientras el TPV tenga la identificación activa.    */
/*                                                                      */
/* Reglas de actuación (en este orden):                                  */
/*   - sin consumidor (ni RFID, ni captura, ni barcode) → no toca nada;  */
/*   - input de un FORMULARIO con foco (no el buscador) y sin captura    */
/*     pendiente → no toca nada: el usuario escribe ahí;                 */
/*   - las primeras teclas de una ráfaga no se pueden prevenir (aún no   */
/*     sabemos que es un lector): se guarda el estado del input con foco */
/*     (valor y selección) al empezar y se RESTAURA al reconocer el      */
/*     escaneo, despachando 'input' para que Angular lo vea;             */
/*   - hace falta velocidad de lector SOSTENIDA (dos intervalos cortos   */
/*     seguidos) para tragarse teclas; si después llega una tecla a      */
/*     velocidad humana, no era un lector: lo tragado se DEVUELVE al     */
/*     input y se empieza de nuevo;                                      */
/*   - Shift/CapsLock (lectores alfanuméricos) no cortan la ráfaga.      */
/************************************************************************/

export const RFID_TAG_LENGTH = 10;          // dígitos de los tags de nuestros lectores de pulseras
const SCAN_INTERVAL_MS = 35;                // Δt máximo entre teclas para ENTRAR en modo lector
const SCAN_ABORT_MS = 90;                   // Δt a mitad de ráfaga que delata tecleo humano (no era lector)
                                            // entre 35 y 90 ms se tolera: jank del hilo principal en un
                                            // datáfono o lectores lentos (20-50 ms/carácter) no parten el código
const SCAN_RESET_MS = 120;                  // Δt sin teclas que cierra/reinicia la ráfaga
const SWALLOW_ENTER_MS = 250;               // ventana para tragarse el Enter que sigue a un tag
const MIN_SILENT_SCAN_LEN = 6;              // sin Enter, una ráfaga más corta no es un escaneo (EAN-8 = 8,
                                            // pulsera = 10): dos teclas rápidas de una persona y una pausa
                                            // devolverían el texto al buscador en vez de "escanearlo"

const RFID_TAG_RE = new RegExp('^[0-9]{' + RFID_TAG_LENGTH + '}$');

type TextInput = HTMLInputElement | HTMLTextAreaElement;

@Injectable()
export class keyboardWedgeService implements OnDestroy {
    private _handler: ((event: KeyboardEvent) => void) = null;
    private _rfidEnabled = false;
    private _buffer = '';
    private _leaked = 0;                    // teclas de la ráfaga que NO se previnieron (prefijo)
    private _swallowed = '';                // teclas tragadas (se devuelven si no era un lector)
    private _fast = 0;                      // intervalos seguidos a velocidad de lector
    private _lastKeyTs = 0;
    private _scannerMode = false;
    // Estado del input con foco al EMPEZAR la ráfaga, para restaurarlo al reconocer
    // el escaneo (las teclas coladas pudieron caer en medio del texto o sobre una selección).
    private _leakInput: TextInput = null;
    private _leakValue = '';
    private _leakSelStart: number = null;
    private _leakSelEnd: number = null;
    private _flushTimer: ReturnType<typeof setTimeout> = null;
    private _swallowEnterUntil = 0;
    private _capture: ((tag: string) => void) = null;
    private _onTag = new Subject<string>();

    constructor(@Optional() private barcode: barcodeService){
        this._handler = (event) => this._onKeyDown(event);
        // Fase de CAPTURA: antes que los listeners de burbujeo y que el input con foco.
        document.addEventListener('keydown', this._handler, true);
    }

    ngOnDestroy(){
        document.removeEventListener('keydown', this._handler, true);
        this._handler = null;
        this.CancelCapture();
        this._reset();
    }

    // Pulsera leída (solo dígitos), una vez por lectura. NO emite mientras hay una
    // captura pendiente (Capture): la lectura es para quien la pidió.
    get OnTag(){
        return this._onTag.asObservable();
    }

    get IsRfidEnabled(){
        return this._rfidEnabled;
    }

    get IsCapturing(){
        return !!this._capture;
    }

    // Identificación por pulsera (OnTag) activa: el TPV la habilita mientras el
    // local está abierto.
    EnableRfid(){
        this._rfidEnabled = true;
    }

    DisableRfid(){
        this._rfidEnabled = false;
        this._reset();
    }

    // Captura EXCLUSIVA de la siguiente pulsera (asignar pulsera en un editor):
    // resuelve con el tag, o con null si se cancela (CancelCapture o una captura
    // nueva). Mientras está pendiente OnTag no emite.
    Capture(): Promise<string> {
        this.CancelCapture();
        return new Promise<string>((resolve) => {
            this._capture = resolve;
        });
    }

    CancelCapture(){
        let _pending = this._capture;
        this._capture = null;
        if (_pending){
            _pending(null);
        }
    }

    // Mientras un editor termina de asignar la pulsera que acaba de capturar (comprobación
    // en el server + confirmación), el lector sigue reemitiendo el tag si la pulsera se
    // queda encima: esas relecturas se DESCARTAN (ni OnTag ni barcode) para que el TPV
    // no identifique/cambie de camarero por detrás.
    private _suppressTags = false;
    SuppressTags(value: boolean){
        this._suppressTags = !!value;
    }

    private _focusedInput(): TextInput | null {
        let _active = document.activeElement;
        if (!_active){
            return null;
        }
        let _tag = _active.tagName;
        if ((_tag === 'INPUT') || (_tag === 'TEXTAREA')){
            return _active as TextInput;
        }
        return null;
    }

    private _reset(){
        this._buffer = '';
        this._leaked = 0;
        this._swallowed = '';
        this._fast = 0;
        this._scannerMode = false;
        this._leakInput = null;
        this._leakValue = '';
        this._leakSelStart = null;
        this._leakSelEnd = null;
        clearTimeout(this._flushTimer);
        this._flushTimer = null;
    }

    // Primera tecla de una ráfaga: aún no sabemos si es un lector, se cuela. Guardamos
    // el estado del input con foco ANTES de que la tecla se aplique (keydown).
    private _startBurst(key: string, input: TextInput | null){
        this._reset();
        this._buffer = key;
        this._leaked = 1;
        if (input){
            this._leakInput = input;
            this._leakValue = input.value;
            try {
                this._leakSelStart = input.selectionStart;
                this._leakSelEnd = input.selectionEnd;
            }
            catch(e){
                this._leakSelStart = null;      // type=number, etc.: sin selección
                this._leakSelEnd = null;
            }
        }
    }

    // Restaura el input con foco tal y como estaba antes de la ráfaga (quita lo colado).
    private _restoreLeaked(){
        let _input = this._leakInput;
        if (!_input || (document.activeElement !== _input)){
            return;
        }
        _input.value = this._leakValue;
        if ((this._leakSelStart !== null) && (this._leakSelEnd !== null)){
            try { _input.setSelectionRange(this._leakSelStart, this._leakSelEnd); } catch(e){ /* sin selección */ }
        }
        _input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // No era un lector (una tecla a velocidad humana tras empezar a tragar): devolver
    // al input lo tragado, en el punto de inserción actual, y olvidar la ráfaga.
    private _abortBurst(){
        let _input = this._leakInput;
        let _swallowed = this._swallowed;
        if (_input && _swallowed && (document.activeElement === _input)){
            let _value = _input.value || '';
            let _start = _value.length;
            let _end = _value.length;
            try {
                if ((_input.selectionStart !== null) && (_input.selectionEnd !== null)){
                    _start = _input.selectionStart;
                    _end = _input.selectionEnd;
                }
            }
            catch(e){ /* sin selección */ }
            _input.value = _value.slice(0, _start) + _swallowed + _value.slice(_end);
            try { _input.setSelectionRange(_start + _swallowed.length, _start + _swallowed.length); } catch(e){ /* sin selección */ }
            _input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        this._reset();
    }

    private _swallow(event: KeyboardEvent){
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    // Fin de ráfaga: recortar lo que se coló en el input con foco, tragarse el Enter
    // que pueda seguir y enrutar el código.
    private _finish(){
        let _code = this._buffer;
        if (this._leaked > 0){
            this._restoreLeaked();
        }
        this._reset();
        this._swallowEnterUntil = Date.now() + SWALLOW_ENTER_MS;

        if (RFID_TAG_RE.test(_code)){
            if (this._suppressTags){
                return;     // relectura durante una asignación en curso
            }
            if (this._capture){
                let _pending = this._capture;
                this._capture = null;
                _pending(_code);
                return;
            }
            if (this._rfidEnabled){
                this._onTag.next(_code);
                return;
            }
        }
        if (this.barcode){
            this.barcode.Inject(_code);
        }
    }

    private _onKeyDown(event: KeyboardEvent){
        // keydowns sintéticos (IME, teclas muertas, extensiones) sin event.key real.
        if (!event || (typeof event.key !== 'string')){
            return;
        }
        if (!this._rfidEnabled && !this._capture && !this._suppressTags && !(this.barcode && this.barcode.IsEnabled)){
            return;     // nadie espera lecturas: no interferir
        }

        // El buscador de la barra (contexto carrito) recibe escaneos; cualquier otro
        // input con foco es un campo de formulario donde el usuario escribe.
        let _input = this._focusedInput();
        if (_input && !_input.closest('ui-searchbar') && !this._capture){
            this._reset();
            return;
        }

        let _now = Date.now();

        // Terminador del lector: Enter o Tab (muchos lectores USB se configuran con sufijo
        // Tab para saltar de campo). Fuera de una ráfaga ninguno de los dos se toca.
        if ((event.key === 'Enter') || (event.key === 'Tab')){
            // Enter/Tab de cola del lector (tras el tag): solo si no ha empezado otra ráfaga.
            if ((_now < this._swallowEnterUntil) && (this._buffer.length === 0)){
                this._swallowEnterUntil = 0;
                this._swallow(event);
                return;
            }
            if (this._scannerMode && (this._buffer.length > 0)){
                this._swallow(event);
                this._finish();
                return;
            }
            this._reset();
            return;
        }

        // Los lectores alfanuméricos "pulsan" Shift para las mayúsculas: no es una tecla
        // del código ni corta la ráfaga.
        if ((event.key === 'Shift') || (event.key === 'CapsLock')){
            return;
        }

        // Solo teclas imprimibles sueltas (sin modificadores) forman parte de un código.
        if ((event.key.length !== 1) || event.ctrlKey || event.altKey || event.metaKey){
            if (this._scannerMode){
                this._abortBurst();
            }
            else {
                this._reset();
            }
            return;
        }

        let _delta = _now - this._lastKeyTs;
        this._lastKeyTs = _now;

        if (_delta > SCAN_RESET_MS){
            // Primera tecla (o tecleo humano): aún no sabemos si es lector. Se cuela.
            if (this._scannerMode){
                this._abortBurst();
            }
            this._startBurst(event.key, _input);
            return;
        }

        if (this._scannerMode && (_delta >= SCAN_ABORT_MS)){
            // Tecla a velocidad humana tras empezar a tragar: no era un lector.
            this._abortBurst();
            this._startBurst(event.key, _input);
            return;
        }

        this._fast = (_delta < SCAN_INTERVAL_MS) ? (this._fast + 1) : 0;
        this._buffer += event.key;

        // Hace falta velocidad SOSTENIDA (dos intervalos cortos seguidos) para tragar:
        // un par de teclas rápidas de una persona no debe secuestrar lo que escribe.
        if (!this._scannerMode){
            if (this._fast < 2){
                this._leaked++;
                return;
            }
            this._scannerMode = true;
        }

        // Es el lector: esta tecla no llega a nadie. Si no manda Enter, la ráfaga se
        // cierra sola tras SCAN_RESET_MS de silencio.
        this._swallowed += event.key;
        this._swallow(event);
        clearTimeout(this._flushTimer);
        this._flushTimer = setTimeout(() => {
            this._flushTimer = null;
            if (this._scannerMode && (this._buffer.length >= MIN_SILENT_SCAN_LEN)){
                this._finish();
            }
            else if (this._scannerMode){
                this._abortBurst();     // ráfaga corta sin Enter: era tecleo, se devuelve al input
            }
            else {
                this._reset();
            }
        }, SCAN_RESET_MS);
    }
}
